import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../cn'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-sm border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
      status: {
        default: 'bg-status-gray-bg text-status-gray',
        green: 'bg-status-green-bg text-status-green',
        amber: 'bg-status-orange-bg text-status-orange',
        blue: 'bg-status-blue-bg text-status-blue',
        red: 'bg-status-red-bg text-status-red',
        purple: 'bg-status-purple-bg text-status-purple',
      },
      type: {
        default: '',
        dot: '!pl-2 !pr-2.5 !py-[3px] !rounded-full !gap-1.5 leading-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      status: 'default',
      type: 'default',
    },
  }
)

const dotVariants = cva('h-1.5 w-1.5 rounded-full', {
  variants: {
    status: {
      default: 'bg-status-gray',
      green: 'bg-status-green',
      amber: 'bg-status-orange',
      blue: 'bg-status-blue',
      red: 'bg-status-red',
      purple: 'bg-status-purple',
    },
  },
  defaultVariants: {
    status: 'default',
  },
})

function Badge({
  className,
  variant,
  asChild = false,
  type,
  status,
  children,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; type?: 'dot' }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot='badge'
      className={cn(badgeVariants({ variant, status, type }), className)}
      {...props}
    >
      {type === 'dot' && <span className={cn(dotVariants({ status }))} />}
      {children}
    </Comp>
  )
}

export { Badge, badgeVariants, dotVariants }
